import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ProductCard } from "../components/product-card";
import { useCart } from "../hooks/use-cart";
import { useTheme } from "../hooks/use-theme";
import { addCartItem } from "../lib/cart";
import { categoryPath, fetchProductDetail, type ProductDetailResponse } from "../lib/catalog";
import { formatCurrency, parseTestedWith, toDarkModeImageUrl, toSentenceCase } from "../lib/utils";

export function ProductPage() {
  const { name, productId } = useParams();
  const theme = useTheme();
  const [data, setData] = useState<ProductDetailResponse | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cartState, setCartState] = useState<"idle" | "adding" | "added">("idle");
  const [cartError, setCartError] = useState<string | null>(null);
  const { addOne, cartCounts } = useCart();

  useEffect(() => {
    let ignore = false;

    async function loadProduct() {
      setLoading(true);
      setError(null);

      try {
        const query = new URLSearchParams();

        if (productId) {
          query.set("id", productId);
        } else if (name) {
          query.set("slug", decodeURIComponent(name));
        }

        const result = await fetchProductDetail(query);
        if (!ignore) {
          setData(result);
          setSelectedImage(result.product.primaryImage);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load this product right now.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadProduct();

    return () => {
      ignore = true;
    };
  }, [name, productId]);

  const product = data?.product;
  const displayName = product ? toSentenceCase(product.name) : toSentenceCase(name || "Product");
  const displayImage =
    theme === "dark" ? toDarkModeImageUrl(selectedImage || product?.primaryImage) : selectedImage || product?.primaryImage;
  const compatibilityTags = parseTestedWith(product?.testedWith);

  async function handleAddToCart() {
    if (!product) {
      return;
    }

    setCartState("adding");
    setCartError(null);

    try {
      await addCartItem({
        productId: product.id,
        quantity: 1
      });
      setCartState("added");
    } catch (addError) {
      setCartState("idle");
      setCartError(addError instanceof Error ? addError.message : "Unable to add item to cart");
    }
  }

  return (
    <section className="mx-auto w-full max-w-[92rem] px-3 py-12 sm:px-4 lg:px-5">
      {loading ? (
        <div className="glass-panel rounded-[32px] p-10 text-sm text-nv-slate">Loading product...</div>
      ) : error || !product ? (
        <div className="glass-panel rounded-[32px] p-10">
          <h1 className="text-3xl font-semibold tracking-tight">Product unavailable</h1>
          <p className="mt-3 text-sm text-nv-slate">{error || "This product could not be found."}</p>
          <Link className="brand-button mt-6 inline-flex rounded-full px-5 py-3 text-sm font-semibold text-white" to="/shop">
            Back to catalog
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-center gap-2 text-sm text-nv-slate">
            <Link className="hover:text-nv-electric" to="/shop">
              Shop
            </Link>
            <span>/</span>
            {product.categoryId ? (
              <Link className="hover:text-nv-electric" to={categoryPath(product.categoryId, product.category)}>
                {product.category}
              </Link>
            ) : (
              <span>{product.category}</span>
            )}
            <span>/</span>
            <span className="text-nv-ink">{displayName}</span>
          </div>

          <div className="grid gap-8 xl:grid-cols-[minmax(0,1.15fr)_420px]">
            <div className="space-y-6">
              <div className="glass-panel overflow-hidden rounded-[36px]">
                <div className="grid gap-0 lg:grid-cols-[120px_minmax(0,1fr)]">
                  <div className="flex gap-3 overflow-auto border-b border-black/5 p-4 lg:flex-col lg:border-b-0 lg:border-r">
                    {(product.gallery.length > 0 ? product.gallery : [{ id: product.id, url: product.primaryImage, altText: product.name }]).map(
                      (image) => {
                        const imageUrl = theme === "dark" ? toDarkModeImageUrl(image.url) || image.url : image.url;

                        return (
                          <button
                            className={`h-20 w-20 shrink-0 overflow-hidden rounded-2xl border ${
                              (selectedImage || product.primaryImage) === image.url
                                ? "border-nv-electric"
                                : "border-black/10"
                            }`}
                            key={image.id}
                            onClick={() => setSelectedImage(image.url)}
                            type="button"
                          >
                            {imageUrl ? (
                              <img alt={image.altText} className="h-full w-full object-cover" src={imageUrl} />
                            ) : (
                              <div className="grid h-full place-items-center text-[10px] text-nv-slate">No image</div>
                            )}
                          </button>
                        );
                      }
                    )}
                  </div>
                  <div className="grid min-h-[420px] place-items-center p-6">
                    {displayImage ? (
                      <img alt={displayName} className="h-full max-h-[540px] w-full object-contain" src={displayImage} />
                    ) : (
                      <div className="text-sm font-medium text-nv-slate">Image coming soon</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="glass-panel rounded-[32px] p-8">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full border border-nv-electric/15 bg-nv-electric/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-nv-electric">
                    {product.category}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      product.inStock
                        ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border border-red-200 bg-red-50 text-red-700"
                    }`}
                  >
                    {product.inStock ? "In stock" : "Out of stock"}
                  </span>
                </div>
                <h1 className="mt-4 text-4xl font-bold tracking-tight">{displayName}</h1>
                <div className="mt-5 grid gap-4 text-sm text-nv-slate sm:grid-cols-2">
                  {product.brand ? (
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">Brand</p>
                      <p className="mt-2 text-base text-nv-ink">{product.brand}</p>
                    </div>
                  ) : null}
                  {product.sku ? (
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em]">SKU</p>
                      <p className="mt-2 text-base text-nv-ink">{product.sku}</p>
                    </div>
                  ) : null}
                </div>
                <div className="mt-8">
                  <h2 className="text-lg font-semibold">Description</h2>
                  <p className="mt-4 whitespace-pre-line leading-8 text-nv-slate">{product.description}</p>
                </div>

                {data.specs.length > 0 ? (
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold">Highlights</h2>
                    <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                      {data.specs.map((item) => (
                        <li className="rounded-2xl border border-black/10 bg-black/5 px-4 py-3 text-sm text-nv-slate" key={item}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {compatibilityTags.length > 0 ? (
                  <div className="mt-8">
                    <h2 className="text-lg font-semibold">Tested with</h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {compatibilityTags.map((tag) => (
                        <span
                          className="rounded-full border border-nv-electric/15 bg-nv-electric/6 px-3 py-1.5 text-sm font-semibold text-nv-electric"
                          key={tag}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <aside className="space-y-6 xl:sticky xl:top-24 xl:self-start">
              <div className="glass-panel rounded-[32px] p-8">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-nv-slate">Purchase</p>
                <div className="mt-4 flex items-end gap-3">
                  <p className="text-4xl font-bold tracking-tight">{formatCurrency(product.price)}</p>
                  {typeof product.minimumPrice === "number" && product.minimumPrice > 0 ? (
                    <span className="pb-1 text-sm text-nv-slate">Min {formatCurrency(product.minimumPrice)}</span>
                  ) : null}
                </div>
                <p className="mt-4 text-sm text-nv-slate">
                  {product.inStock
                    ? "Available for order now. Inventory and pricing reflect the current catalog."
                    : "Currently unavailable. You can still review the specifications and related alternatives."}
                </p>
                <div className="mt-6 grid gap-3">
                  <div className="flex items-center gap-3">
                    {cartCounts.get(product.id) ? (
                      <Link
                        className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-nv-electric/15 bg-nv-electric/6 text-nv-electric"
                        title="View cart"
                        to="/shop/cart"
                      >
                        <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                          <path d="M3 4h2l2.1 9.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 7H7.2" />
                          <circle cx="10" cy="19" r="1.5" />
                          <circle cx="17" cy="19" r="1.5" />
                        </svg>
                        <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-nv-accent px-1 text-[10px] font-bold leading-none text-white">
                          {cartCounts.get(product.id)}
                        </span>
                      </Link>
                    ) : null}
                    <button
                      className="brand-button flex-1 rounded-full px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={!product.inStock}
                      onClick={() => void handleAddToCart()}
                      type="button"
                    >
                      {cartState === "adding" ? "Adding..." : cartState === "added" ? "Added to cart" : "Add to cart"}
                    </button>
                  </div>
                  <Link
                    className="rounded-full border border-black/10 px-5 py-3 text-center text-sm font-semibold"
                    to="/shop/cart"
                  >
                    View cart
                  </Link>
                  {product.codeUrl ? (
                    <a
                      className="rounded-full border border-black/10 px-5 py-3 text-center text-sm font-semibold"
                      href={product.codeUrl}
                      rel="noreferrer"
                      target="_blank"
                    >
                      View code resources
                    </a>
                  ) : null}
                </div>
                {cartError ? <p className="mt-4 text-sm text-red-600">{cartError}</p> : null}
                <div className="mt-6 grid gap-4 border-t border-black/10 pt-6 text-sm text-nv-slate">
                  <div className="flex items-center justify-between gap-4">
                    <span>Availability</span>
                    <span className="font-semibold text-nv-ink">{product.inStock ? "In stock" : "Out of stock"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span>Category</span>
                    <span className="font-semibold text-nv-ink">{product.category}</span>
                  </div>
                  {product.brand ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>Brand</span>
                      <span className="font-semibold text-nv-ink">{product.brand}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </aside>
          </div>

          {data.related.length > 0 ? (
            <div className="mt-12">
              <div className="mb-6 flex items-end justify-between gap-4">
                <div>
                  <p className="eyebrow">Related items</p>
                  <h2 className="mt-2 text-3xl font-semibold tracking-tight">Similar picks from the catalog</h2>
                </div>
                {product.categoryId ? (
                  <Link className="text-sm font-semibold text-nv-electric" to={categoryPath(product.categoryId, product.category)}>
                    View more in {product.category}
                  </Link>
                ) : null}
              </div>
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                {data.related.map((item) => (
                  <ProductCard
                    cartQuantity={cartCounts.get(item.id) || 0}
                    category={item.category}
                    description={item.description}
                    id={item.id}
                    image={item.image}
                    key={item.id}
                    name={item.name}
                    price={item.price}
                    quantity={item.quantity}
                    slug={item.slug}
                    testedWith={item.testedWith}
                    onAddToCart={addOne}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
